import React, { useState, useEffect, useRef } from 'react'
import {
  ScrollArea,
  TextInput,
  ActionIcon,
  Group,
  Text,
  Avatar,
  Stack,
  useMantineTheme,
  Loader,
  Tooltip,
  createStyles,
  Button,
  Menu,
  Modal,
  PasswordInput
} from '@mantine/core'
import { openConfirmModal } from '@mantine/modals'
import {
  IconSend,
  IconRotate,
  IconMicrophone,
  IconPlayerStop,
  IconSettings,
  IconCopy,
  IconCheck,
  IconLogout,
  IconFolder,
  IconMessage2
} from '@tabler/icons'
import { IconSparkles } from '@tabler/icons-react'
import { useStore } from '../../hooks/useStore'
import { useSessionStorage, useLocalStorage } from '@mantine/hooks'
import { useSpeechToText } from '../../hooks/useSpeechToText'
import { useOllama, OLLAMA_MISSING_CREDENTIALS_ERROR } from '../../hooks/useOllama'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { skills, generateSystemPrompt } from '../../skills'
import { getAuth, signOut } from 'firebase/auth'
import { useNavigate } from 'react-router-dom'
import logo from '../../assets/logo.png'

interface Message {
  id: string
  sender: 'user' | 'ai' | 'tool'
  text: string
  timestamp: Date | string
  toolName?: string
  toolStatus?: 'running' | 'success' | 'error'
  toolArgs?: any
}

const useStyles = createStyles((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    width: '100vw',
    backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[8] : theme.white,
    color: theme.colorScheme === 'dark' ? theme.white : theme.black,
    position: 'relative',
    overflow: 'hidden'
  },
  header: {
    height: '60px',
    padding: '0 16px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottom: `1px solid ${theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.gray[2]}`,
    backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[7] : theme.white,
    zIndex: 10
  },
  content: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    height: 'calc(100vh - 60px - 80px)', // Account for header and floating nav
    overflow: 'hidden'
  },
  blankTab: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '24px',
    textAlign: 'center'
  },
  bottomNav: {
    position: 'fixed',
    bottom: '16px',
    left: '16px',
    right: '16px',
    height: '64px',
    borderRadius: '32px',
    backgroundColor: theme.colorScheme === 'dark' ? 'rgba(26, 27, 30, 0.85)' : 'rgba(255, 255, 255, 0.85)',
    backdropFilter: 'blur(12px)',
    border: `1px solid ${theme.colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'}`,
    display: 'flex',
    justifyContent: 'space-around',
    alignItems: 'center',
    zIndex: 1000,
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.25)'
  },
  navButton: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'transparent',
    border: 'none',
    color: theme.colors.gray[5],
    cursor: 'pointer',
    padding: '8px 16px',
    borderRadius: '16px',
    transition: 'all 0.2s ease',
    '&:active': {
      transform: 'scale(0.95)'
    }
  },
  navButtonActive: {
    color: theme.colors.green[6]
  },
  chatBubble: {
    padding: '10px 14px',
    borderRadius: '16px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
    minWidth: 0,
    maxWidth: '100%',
    wordBreak: 'break-word',
    overflowWrap: 'break-word'
  },
  userBubble: {
    backgroundColor: theme.colors.green[8],
    color: theme.white,
    alignSelf: 'flex-end',
    borderRadius: '16px 16px 2px 16px'
  },
  aiBubble: {
    backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.gray[1],
    color: theme.colorScheme === 'dark' ? theme.colors.dark[0] : theme.black,
    alignSelf: 'flex-start',
    borderRadius: '16px 16px 16px 2px'
  },
  toolBubble: {
    backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.gray[1],
    color: theme.colorScheme === 'dark' ? theme.colors.dark[0] : theme.black,
    alignSelf: 'flex-start',
    borderRadius: '16px 16px 16px 2px',
    borderLeft: `3px solid ${theme.colors.blue[6]}`,
    padding: '10px 14px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    minWidth: 0
  }
}))

export default function MobileView() {
  const { classes } = useStyles()
  const theme = useMantineTheme()
  const navigate = useNavigate()
  const auth = getAuth()

  // Tab State: 'ai' | 'docs'
  const [activeTab, setActiveTab] = useState<'ai' | 'docs'>('ai')

  // Chat States & Logic
  const [inputValue, setInputValue] = useState('')
  const [isTyping, setIsTyping] = useState(false)
  const [hoveredMessageId, setHoveredMessageId] = useState<string | null>(null)
  const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null)
  const scrollViewportRef = useRef<HTMLDivElement>(null)
  const recursionCountRef = useRef<number>(0)
  const processedPromptRef = useRef<string>('')

  const pendingAiPrompt = useStore((state: any) => state.pendingAiPrompt)
  const setPendingAiPrompt = useStore((state: any) => state.setPendingAiPrompt)

  const [settingsModalOpened, setSettingsModalOpened] = useState(false)
  const [apiKey, setApiKey] = useLocalStorage<string>({
    key: 'ai_api_key',
    defaultValue: '',
  })
  const [modelTag, setModelTag] = useLocalStorage<string>({
    key: 'ai_model_tag',
    defaultValue: '',
  })

  const { isListening, toggleListening } = useSpeechToText((transcript) => {
    setInputValue((prev) => (prev ? `${prev} ${transcript}` : transcript))
  })

  const { streamChat, isLoading, stopStreaming } = useOllama()

  const getLocalStorageValue = (key: string, defaultValue: string = ''): string => {
    const val = localStorage.getItem(key)
    if (!val) return defaultValue
    try {
      return JSON.parse(val)
    } catch {
      return val
    }
  }

  const generateUniqueId = (): string => {
    return `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`
  }

  const [messages, setMessages] = useSessionStorage<Message[]>({
    key: 'aiSideBarConversationHistory',
    defaultValue: [
      {
        id: '1',
        sender: 'ai',
        text: 'Szia! Én vagyok a Builty AI asszisztensed. Miben segíthetek ma?',
        timestamp: new Date(Date.now() - 60000 * 5)
      }
    ],
    deserialize: (str) => {
      if (!str) return []
      try {
        const parsed = JSON.parse(str) as any[]
        return parsed.map((msg) => ({
          ...msg,
          timestamp: new Date(msg.timestamp)
        }))
      } catch {
        return []
      }
    }
  })

  const scrollToBottom = (behavior: 'smooth' | 'auto' = 'smooth') => {
    if (scrollViewportRef.current) {
      scrollViewportRef.current.scrollTo({
        top: scrollViewportRef.current.scrollHeight,
        behavior
      })
    }
  }

  const handleCopyText = (msgId: string, text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedMessageId(msgId)
      setTimeout(() => {
        setCopiedMessageId(null)
      }, 2000)
    }).catch((err) => {
      console.error('Nem sikerült a szöveg másolása:', err)
    })
  }

  useEffect(() => {
    if (activeTab === 'ai') {
      scrollToBottom('auto')
      const t = setTimeout(() => scrollToBottom('smooth'), 100)
      return () => clearTimeout(t)
    }
  }, [activeTab])

  useEffect(() => {
    scrollToBottom('smooth')
  }, [messages, isTyping])

  useEffect(() => {
    if (!pendingAiPrompt) {
      processedPromptRef.current = ''
      return
    }

    if (pendingAiPrompt.trim() && processedPromptRef.current !== pendingAiPrompt) {
      processedPromptRef.current = pendingAiPrompt
      const promptText = pendingAiPrompt.trim()

      const userMsg: Message = {
        id: generateUniqueId(),
        sender: 'user',
        text: promptText,
        timestamp: new Date()
      }

      setPendingAiPrompt('')
      recursionCountRef.current = 0

      const keyVal = localStorage.getItem('ai_api_key')
      const tagVal = localStorage.getItem('ai_model_tag')
      const activeKey = keyVal ? JSON.parse(keyVal) : ''
      const activeTag = tagVal ? JSON.parse(tagVal) : ''

      if (!activeKey.trim() || !activeTag.trim()) {
        setIsTyping(false)
        setMessages((prev) => [
          ...prev,
          userMsg,
          {
            id: generateUniqueId(),
            sender: 'ai',
            text: OLLAMA_MISSING_CREDENTIALS_ERROR,
            timestamp: new Date()
          }
        ])
        return
      }

      const updated = [...messages, userMsg]
      setMessages(updated)
      startStreamingResponse(updated)
    }
  }, [pendingAiPrompt, setPendingAiPrompt])

  const startStreamingResponse = (updatedMessages: Message[]) => {
    setIsTyping(true)
    const aiMessageId = generateUniqueId()

    const systemPrompt = generateSystemPrompt({ isMobile: true })
    const ollamaMessages = [
      { role: 'system', content: systemPrompt },
      ...updatedMessages.map((msg) => {
        if (msg.sender === 'tool') {
          return {
            role: 'user',
            content: `[ESZKÖZ VÁLASZA - ${msg.toolName}]: ${msg.text}`
          }
        }
        return {
          role: msg.sender === 'user' ? 'user' : 'assistant',
          content: msg.text
        }
      })
    ]

    streamChat(ollamaMessages, {
      onChunk: (chunk) => {
        setIsTyping(false)
        setMessages((mList) => {
          const exists = mList.some((msg) => msg.id === aiMessageId)
          if (!exists) {
            return [
              ...mList,
              {
                id: aiMessageId,
                sender: 'ai',
                text: chunk,
                timestamp: new Date()
              }
            ]
          }
          return mList.map((msg) =>
            msg.id === aiMessageId ? { ...msg, text: msg.text + chunk } : msg
          )
        })
      },
      onDone: async (fullText) => {
        setIsTyping(false)

        const finalAiMsg: Message = {
          id: aiMessageId,
          sender: 'ai',
          text: fullText,
          timestamp: new Date()
        }
        const listWithFinalAi = [...updatedMessages, finalAiMsg]
        setMessages(listWithFinalAi)

        const match = fullText.match(/EXECUTE:(\w+)\(([\s\S]*?)\)/)
        if (match) {
          const toolName = match[1]
          const argsStr = match[2].trim()
          let args = {}
          if (argsStr) {
            try {
              args = JSON.parse(argsStr)
            } catch (e) {
              console.error("Nem sikerült JSON argumentumot parse-olni:", argsStr, e)
            }
          }

          if (recursionCountRef.current >= 5) {
            console.warn("Max recursion limit reached.")
            return
          }

          const skill = skills[toolName]
          if (skill) {
            recursionCountRef.current += 1
            const toolMsgId = generateUniqueId()

            const runningToolMsg: Message = {
              id: toolMsgId,
              sender: 'tool',
              text: 'Futás...',
              toolName: toolName,
              toolStatus: 'running',
              toolArgs: args,
              timestamp: new Date()
            }
            const listWithRunning = [...listWithFinalAi, runningToolMsg]
            setMessages(listWithRunning)

            const runTool = async () => {
              try {
                const result = await skill.execute(args)
                const resultString = typeof result === 'string' ? result : JSON.stringify(result)

                const listWithSuccess = listWithRunning.map((msg) =>
                  msg.id === toolMsgId
                    ? { ...msg, text: resultString, toolStatus: 'success' as const }
                    : msg
                )
                setMessages(listWithSuccess)
                startStreamingResponse(listWithSuccess)
              } catch (err: any) {
                console.error(`Hiba a(z) ${toolName} futtatásakor:`, err)
                const errStr = err?.message || String(err)

                const listWithError = listWithRunning.map((msg) =>
                  msg.id === toolMsgId
                    ? { ...msg, text: `Hiba: ${errStr}`, toolStatus: 'error' as const }
                    : msg
                )
                setMessages(listWithError)
                startStreamingResponse(listWithError)
              }
            }

            runTool()
          }
        }
      },
      onError: (error) => {
        setIsTyping(false)
        setMessages((mList) => [
          ...mList,
          {
            id: generateUniqueId(),
            sender: 'ai',
            text: `Hiba történt: ${error}`,
            timestamp: new Date()
          }
        ])
      }
    })
  }

  const handleSendMessage = () => {
    if (!inputValue.trim() || isLoading) return

    const userMsg: Message = {
      id: generateUniqueId(),
      sender: 'user',
      text: inputValue.trim(),
      timestamp: new Date()
    }

    setInputValue('')
    recursionCountRef.current = 0

    const apiKey = getLocalStorageValue('ai_api_key')
    const modelTag = getLocalStorageValue('ai_model_tag')

    if (!apiKey.trim() || !modelTag.trim()) {
      setIsTyping(false)
      setMessages((prev) => [
        ...prev,
        userMsg,
        {
          id: generateUniqueId(),
          sender: 'ai',
          text: OLLAMA_MISSING_CREDENTIALS_ERROR,
          timestamp: new Date()
        }
      ])
      return
    }

    const updated = [...messages, userMsg]
    setMessages(updated)
    startStreamingResponse(updated)
  }

  const handleClearHistory = () => {
    openConfirmModal({
      title: 'Beszélgetés törlése',
      centered: true,
      children: (
        <Text size="sm">
          Biztosan törölni szeretnéd a beszélgetési előzményeket? Ezt a műveletet nem lehet visszavonni.
        </Text>
      ),
      labels: { confirm: 'Törlés', cancel: 'Mégsem' },
      confirmProps: { color: 'red' },
      onConfirm: () => {
        setMessages([
          {
            id: generateUniqueId(),
            sender: 'ai',
            text: 'Szia! Én vagyok a Builty AI asszisztensed. Miben segíthetek ma?',
            timestamp: new Date()
          }
        ])
      }
    })
  }

  const signout_from_firebase = () => {
    signOut(auth).then(() => {
      localStorage.removeItem("user")
      localStorage.removeItem("photoURL")
      localStorage.removeItem("displayName")
      sessionStorage.clear()
      navigate("/login")
    }).catch((error: Error) => {
      console.error("Signout error:", error)
    })
  }

  const photoURL = localStorage.getItem("photoURL")
  const displayName = localStorage.getItem("displayName")
  const userPhoto = photoURL ? JSON.parse(photoURL) : ""
  const userName = displayName ? JSON.parse(displayName) : "Felhasználó"

  return (
    <div className={classes.container}>
      {/* Mobile Top Header */}
      <header className={classes.header}>
        <Group spacing="xs">
          <img src={logo} alt="Builty" style={{ height: '24px' }} />
          <Text size="sm" color="dimmed" weight={700}>Mobil</Text>
        </Group>

        <Group spacing="md">
          {activeTab === 'ai' && (
            <>
              <ActionIcon onClick={() => setSettingsModalOpened(true)} title="AI Beállítások" variant="subtle" color="gray">
                <IconSettings size={18} />
              </ActionIcon>
              <ActionIcon onClick={handleClearHistory} title="Csevegés alaphelyzetbe állítása" variant="subtle" color="gray">
                <IconRotate size={18} />
              </ActionIcon>
            </>
          )}
          <Menu position="bottom-end" transition="pop-top-right">
            <Menu.Target>
              <Avatar src={userPhoto} alt={userName} radius="xl" size={28} style={{ cursor: 'pointer' }} />
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Label>Fiók ({userName})</Menu.Label>
              <Menu.Item onClick={signout_from_firebase} color="red" icon={<IconLogout size={14} />}>
                Kijelentkezés
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Group>
      </header>

      {/* Main Tabs Content */}
      <main className={classes.content}>
        {activeTab === 'ai' ? (
          <div style={{ display: 'flex', flexDirection: 'column', height: '100%', paddingBottom: '88px' }}>
            {/* Scrollable Message Area */}
            <ScrollArea style={{ flex: 1, padding: '16px' }} viewportRef={scrollViewportRef}>
              <Stack spacing="md">
                {messages.map((msg) => {
                  if (msg.text.includes('EXECUTE:')) return null

                  const isUser = msg.sender === 'user'
                  const isTool = msg.sender === 'tool'

                  if (isTool) {
                    const skill = msg.toolName ? skills[msg.toolName] : null
                    const uiText = skill ? skill.outputTextForUI : (msg.toolName || 'Eszköz')
                    let statusText = 'futtatás...'
                    if (msg.toolStatus === 'success') statusText = 'Sikeres'
                    if (msg.toolStatus === 'error') statusText = 'Hiba'

                    const argsStr = msg.toolArgs ? JSON.stringify(msg.toolArgs) : ''
                    const hasArgs = argsStr && argsStr !== '{}' && Object.keys(msg.toolArgs).length > 0

                    return (
                      <div key={msg.id} style={{ display: 'flex', justifyContent: 'flex-start', width: '100%' }}>
                        <div style={{ display: 'flex', gap: '8px', maxWidth: '85%', minWidth: 0 }}>
                          <Avatar color="blue" radius="xl" size="xs" style={{ marginTop: '4px' }}>
                            <IconSettings size={12} />
                          </Avatar>
                          <div className={classes.toolBubble} style={{ fontSize: '12px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              {msg.toolStatus === 'running' && <Loader size="xs" color="blue" variant="dots" />}
                              <Text size="xs" weight={700}>{uiText}: {statusText}</Text>
                            </div>
                            {hasArgs && (
                              <Text size="xs" color="dimmed" style={{ fontStyle: 'italic', wordBreak: 'break-all' }}>
                                Paraméterek: {argsStr}
                              </Text>
                            )}
                          </div>
                        </div>
                      </div>
                    )
                  }

                  return (
                    <div
                      key={msg.id}
                      style={{
                        display: 'flex',
                        justifyContent: isUser ? 'flex-end' : 'flex-start',
                        width: '100%'
                      }}
                    >
                      <div
                        onMouseEnter={() => setHoveredMessageId(msg.id)}
                        onMouseLeave={() => setHoveredMessageId(null)}
                        style={{
                          display: 'flex',
                          flexDirection: isUser ? 'row-reverse' : 'row',
                          alignItems: 'flex-start',
                          gap: '8px',
                          maxWidth: '85%',
                          minWidth: 0,
                          position: 'relative'
                        }}
                      >
                        {!isUser && (
                          <Avatar color="green" radius="xl" size="xs" style={{ marginTop: '4px' }}>
                            <IconSparkles size={12} />
                          </Avatar>
                        )}
                        <div className={`${classes.chatBubble} ${isUser ? classes.userBubble : classes.aiBubble}`}>
                          {isUser ? (
                            <Text size="sm" style={{ whiteSpace: 'pre-line' }}>{msg.text}</Text>
                          ) : (
                            <div className="markdown-body" style={{ fontSize: theme.fontSizes.sm }}>
                              <ReactMarkdown
                                remarkPlugins={[remarkGfm]}
                                components={{
                                  p: ({ children }) => <p style={{ margin: 0, whiteSpace: 'pre-line', overflowWrap: 'break-word', wordBreak: 'break-word' }}>{children}</p>,
                                  ul: ({ children }) => <ul style={{ margin: '4px 0', paddingLeft: '20px' }}>{children}</ul>,
                                  ol: ({ children }) => <ol style={{ margin: '4px 0', paddingLeft: '20px' }}>{children}</ol>,
                                  li: ({ children }) => <li style={{ margin: '2px 0', overflowWrap: 'break-word', wordBreak: 'break-word' }}>{children}</li>,
                                  h1: ({ children }) => <Text size="md" weight={700} style={{ margin: '8px 0 4px 0', overflowWrap: 'break-word', wordBreak: 'break-word' }}>{children}</Text>,
                                  h2: ({ children }) => <Text size="sm" weight={700} style={{ margin: '8px 0 4px 0', overflowWrap: 'break-word', wordBreak: 'break-word' }}>{children}</Text>,
                                  h3: ({ children }) => <Text size="sm" weight={700} style={{ margin: '8px 0 4px 0', overflowWrap: 'break-word', wordBreak: 'break-word' }}>{children}</Text>,
                                  code: ({ children }) => (
                                    <code style={{
                                      wordBreak: 'break-all',
                                      whiteSpace: 'pre-wrap',
                                      backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[8] : theme.colors.gray[2],
                                      padding: '2px 4px',
                                      borderRadius: '4px',
                                      fontSize: '90%'
                                    }}>
                                      {children}
                                    </code>
                                  ),
                                  pre: ({ children }) => (
                                    <pre style={{
                                      margin: '8px 0',
                                      padding: '10px',
                                      backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[8] : theme.colors.gray[2],
                                      borderRadius: '6px',
                                      overflowX: 'auto',
                                      maxWidth: '100%'
                                    }}>
                                      {children}
                                    </pre>
                                  ),
                                  table: ({ children }) => (
                                    <div style={{
                                      overflowX: 'auto',
                                      margin: '12px 0',
                                      maxWidth: '100%',
                                      borderRadius: '8px',
                                      border: `1px solid ${theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[3]}`
                                    }}>
                                      <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '13px' }}>
                                        {children}
                                      </table>
                                    </div>
                                  ),
                                  thead: ({ children }) => (
                                    <thead style={{ backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[8] : theme.colors.gray[2] }}>
                                      {children}
                                    </thead>
                                  ),
                                  tbody: ({ children }) => <tbody>{children}</tbody>,
                                  tr: ({ children }) => (
                                    <tr style={{
                                      borderBottom: `1px solid ${theme.colorScheme === 'dark' ? theme.colors.dark[5] : theme.colors.gray[3]}`,
                                      backgroundColor: 'transparent'
                                    }}>
                                      {children}
                                    </tr>
                                  ),
                                  th: ({ children }) => (
                                    <th style={{ padding: '8px 12px', fontWeight: 600, color: theme.colorScheme === 'dark' ? theme.white : theme.black }}>
                                      {children}
                                    </th>
                                  ),
                                  td: ({ children }) => (
                                    <td style={{ padding: '8px 12px', color: theme.colorScheme === 'dark' ? theme.colors.dark[1] : theme.colors.gray[8] }}>
                                      {children}
                                    </td>
                                  ),
                                }}
                              >
                                {msg.text}
                              </ReactMarkdown>
                            </div>
                          )}
                          <Text
                            size="xs"
                            align={isUser ? 'right' : 'left'}
                            style={{
                              fontSize: '9px',
                              marginTop: '4px',
                              color: isUser ? 'rgba(255, 255, 255, 0.7)' : theme.colors.gray[5]
                            }}
                          >
                            {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </Text>
                        </div>
                        <ActionIcon
                          size="xs"
                          variant="subtle"
                          color="gray"
                          onClick={() => handleCopyText(msg.id, msg.text)}
                          style={{
                            position: 'absolute',
                            right: isUser ? 'auto' : '-24px',
                            left: isUser ? '-24px' : 'auto',
                            top: '8px'
                          }}
                        >
                          {copiedMessageId === msg.id ? <IconCheck size={12} color={theme.colors.green[6]} /> : <IconCopy size={12} />}
                        </ActionIcon>
                      </div>
                    </div>
                  )
                })}

                {isTyping && (
                  <div style={{ display: 'flex', justifyContent: 'flex-start', width: '100%' }}>
                    <div style={{ display: 'flex', gap: '8px', maxWidth: '85%' }}>
                      <Avatar color="green" radius="xl" size="xs" style={{ marginTop: '4px' }}>
                        <IconSparkles size={12} />
                      </Avatar>
                      <div className={`${classes.chatBubble} ${classes.aiBubble}`} style={{ minWidth: '50px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Loader size="xs" variant="dots" color="green" />
                      </div>
                    </div>
                  </div>
                )}
              </Stack>
            </ScrollArea>

            {/* Chat Input */}
            <div style={{ padding: '10px 16px', backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[8] : theme.white }}>
              <TextInput
                placeholder="Kérdezz bármit..."
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !isLoading) handleSendMessage()
                }}
                icon={
                  <Tooltip label={isListening ? "Diktálás leállítása" : "Hangalapú kérdezés"} withArrow position="top">
                    <ActionIcon
                      variant="subtle"
                      color={isListening ? "red" : "gray"}
                      radius="xl"
                      size="sm"
                      onClick={toggleListening}
                    >
                      <IconMicrophone size={16} color={isListening ? theme.colors.red[6] : undefined} />
                    </ActionIcon>
                  </Tooltip>
                }
                rightSection={
                  isLoading ? (
                    <Tooltip label="Leállítás" withArrow position="top">
                      <ActionIcon onClick={stopStreaming} variant="filled" color="red" radius="xl" size="sm">
                        <IconPlayerStop size={12} />
                      </ActionIcon>
                    </Tooltip>
                  ) : (
                    <Tooltip label="Küldés" withArrow position="top">
                      <ActionIcon onClick={handleSendMessage} variant="filled" color="green" radius="xl" size="sm" disabled={!inputValue.trim()}>
                        <IconSend size={12} />
                      </ActionIcon>
                    </Tooltip>
                  )
                }
                rightSectionWidth={42}
                radius="xl"
                size="md"
                styles={{
                  input: {
                    paddingRight: '48px',
                    borderColor: theme.colorScheme === 'dark' ? theme.colors.dark[5] : theme.colors.gray[3]
                  },
                  icon: {
                    pointerEvents: 'all'
                  }
                }}
              />
            </div>
          </div>
        ) : (
          /* Blank Dokumentumtár View */
          <div className={classes.blankTab} style={{ paddingBottom: '88px' }}>
            <IconFolder size={48} stroke={1.5} color={theme.colors.green[6]} style={{ marginBottom: '16px', opacity: 0.8 }} />
            <Text weight={700} size="lg" style={{ marginBottom: '8px' }}>Dokumentumtár</Text>
            <Text size="sm" color="dimmed" lineClamp={2}>
              Ez a fül jelenleg üres. Később ide kerülnek a dokumentumokkal kapcsolatos funkciók.
            </Text>
          </div>
        )}
      </main>

      {/* Floating Bottom Nav */}
      <nav className={classes.bottomNav}>
        <button
          className={`${classes.navButton} ${activeTab === 'ai' ? classes.navButtonActive : ''}`}
          onClick={() => setActiveTab('ai')}
        >
          <IconMessage2 size={20} style={{ marginBottom: '4px' }} />
          <Text size="xs" weight={activeTab === 'ai' ? 700 : 500}>Builty AI</Text>
        </button>

        <button
          className={`${classes.navButton} ${activeTab === 'docs' ? classes.navButtonActive : ''}`}
          onClick={() => setActiveTab('docs')}
        >
          <IconFolder size={20} style={{ marginBottom: '4px' }} />
          <Text size="xs" weight={activeTab === 'docs' ? 700 : 500}>Dokumentumtár</Text>
        </button>
      </nav>

      {/* AI Settings Modal */}
      <Modal
        opened={settingsModalOpened}
        onClose={() => setSettingsModalOpened(false)}
        title="AI Beállítások"
        centered
        size="90%"
        styles={(theme) => ({
          modal: {
            backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[7] : theme.white,
          }
        })}
      >
        <Stack spacing="md">
          <PasswordInput
            label="API kulcs"
            placeholder="Írd be az API kulcsodat"
            value={apiKey}
            onChange={(event) => setApiKey(event.currentTarget.value)}
            description="Az Ollama Cloud eléréséhez szükséges API kulcs."
          />
          <TextInput
            label="Modell tag"
            placeholder="pl. llama3, mistral, deepseek-coder"
            value={modelTag}
            onChange={(event) => setModelTag(event.currentTarget.value)}
            description="A használni kívánt modell azonosítója (tag)."
          />
          <Button color="green" fullWidth onClick={() => setSettingsModalOpened(false)}>
            Mentés
          </Button>
        </Stack>
      </Modal>
    </div>
  )
}
